import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
// Thème unique « Cream & Ink » : plus de mode sombre ni de fond shader.
import type { AppStep, AnalysisOption, ComplianceAnalysisResult, SocialPostMeta, AnalysisType, CreativeIdea, SuggestedFix, EventActivationMeta, ProductCategory } from '../shared/types';
import { ANALYSIS_OPTIONS } from '../shared/types';
import { useAnalysisJobs, type SidebarItem, type AppNotification } from './hooks/useAnalysisJobs';
import { primeAudio } from './utils/sound';
import { useAuth } from './contexts/AuthContext';
import AuthGate from './components/AuthGate';
import TermsGate from './components/TermsGate';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import AccountPopover from './components/AccountPopover';
import AnalysisTypeSelector from './components/AnalysisTypeSelector';
import ContentUploader from './components/ContentUploader';
import CreativeBriefForm from './components/CreativeBriefForm';
import SocialPostHub from './components/SocialPostHub';
import HistoriquePage from './components/HistoriquePage';
import Loader from './components/Loader';
import ResultsDisplay from './components/ResultsDisplay';
import CreativeResults from './components/CreativeResults';
import PricingPage from './components/PricingPage';
import OnboardingScreen from './components/OnboardingScreen';
import AccountSettingsPage from './components/AccountSettingsPage';
import DisclaimerModal from './components/DisclaimerModal';
import LegalNotice from './components/LegalNotice';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Subprocessors from './components/Subprocessors';
import Footer from './components/Footer';
import AnalysisToast from './components/AnalysisToast';
import MobileTabBar from './components/MobileTabBar';
import type { SidebarNav } from './components/Sidebar';
import { tintVars, APP_SURFACE } from './utils/tints';

const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

/** Charge utile soumise par les formulaires d'analyse. */
type SubmitData = {
  textContent?: string;
  context?: string;
  images?: File[];
  social?: SocialPostMeta;
  /** Qualification réglementaire du produit (étape zéro). Défaut : 'undecided'. */
  productCategory?: ProductCategory;
  event?: EventActivationMeta;
};

/** Construit le résumé d'entrée (titre de la discussion) — même logique que
 *  l'ancien addToHistory, dont le cas spécial creative_brief. */
function buildInputSummary(option: AnalysisOption, data: SubmitData): string {
  let inputSummary =
    data.textContent ||
    (data.images && data.images.length > 0 ? data.images.map((f) => f.name).join(', ') : option.label);
  if (option.key === 'creative_brief' && data.textContent) {
    try {
      const brief = JSON.parse(data.textContent);
      inputSummary = [brief.brand, brief.product].filter(Boolean).join(' — ') || option.label;
    } catch {
      /* keep raw */
    }
  }
  return inputSummary;
}

export default function App() {
  const { features, organization, isOnboarding, refreshProfile } = useAuth();
  const jobs = useAnalysisJobs();

  const [step, setStep] = useState<AppStep>('select');
  const [selectedOption, setSelectedOption] = useState<AnalysisOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Affichage d'une analyse ouverte depuis l'Historique serveur (lecture seule,
  // non ancrée à un item de la sidebar — comme l'ancien comportement).
  const [externalResult, setExternalResult] = useState<{ result: ComplianceAnalysisResult; option: AnalysisOption | null } | null>(null);
  const [legalVisible, setLegalVisible] = useState(false);
  const [privacyVisible, setPrivacyVisible] = useState(false);
  const [termsVisible, setTermsVisible] = useState(false);
  const [subprocessorsVisible, setSubprocessorsVisible] = useState(false);

  // Sidebar / chrome state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [accountInitialTab, setAccountInitialTab] = useState<'menu' | 'scoring' | 'transparency'>('menu');
  const [pricingScrollTo, setPricingScrollTo] = useState<string | undefined>();

  const activeItem = jobs.items.find((i) => i.id === jobs.activeId) ?? null;
  const activeOption = activeItem ? ANALYSIS_OPTIONS.find((o) => o.key === activeItem.analysisType) ?? null : null;

  // Si l'utilisateur regarde déjà une analyse qui vient de se terminer, on
  // marque sa notification comme lue (pas de pastille fantôme pour ce qu'il voit).
  const viewingAnalysis = step === 'result' || step === 'loading';
  const activeItemDoneId = activeItem && activeItem.status === 'done' && viewingAnalysis ? activeItem.id : null;
  useEffect(() => {
    if (activeItemDoneId) jobs.markReadByItem(activeItemDoneId);
    // jobs.markReadByItem volontairement hors deps (identité instable, no-op si rien à lire)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemDoneId]);

  const handleSelectType = useCallback(
    (option: AnalysisOption) => {
      setSelectedOption(option);
      setError(null);
      setExternalResult(null);
      jobs.setActiveId(null);
      setStep('upload');
    },
    [jobs],
  );

  // Met une analyse en file d'attente. La discussion apparaît instantanément
  // dans la sidebar (item 'queued'/'running'), l'écran bascule sur le loader,
  // mais le résultat ne s'affiche PAS de force : à la fin, une notification est
  // émise. Si l'utilisateur reste sur cet écran, il voit le résultat arriver.
  const handleSubmit = useCallback(
    (data: SubmitData) => {
      if (!selectedOption) return;
      primeAudio(); // débloque l'audio dans le geste utilisateur (son de fin d'analyse)
      const inputSummary = buildInputSummary(selectedOption, data);
      const id = jobs.enqueue({
        analysisType: selectedOption.key,
        label: selectedOption.label,
        inputSummary,
        payload: data,
      });
      setExternalResult(null);
      jobs.setActiveId(id);
      setStep('result');
      setError(null);
    },
    [selectedOption, jobs],
  );

  // Deep analyze d'une idée créative (chemin Visaly Créa — actuellement masqué).
  const handleDeepAnalyze = useCallback(
    (idea: CreativeIdea) => {
      const conceptOption = ANALYSIS_OPTIONS.find((o) => o.key === 'concept')!;
      const textContent = [
        `TERRITOIRE CRÉATIF : ${idea.territory}`,
        `\nCONCEPT : ${idea.concept}`,
        `\nSLOGANS PROPOSÉS :\n${idea.slogans.map((s, i) => `${i + 1}. « ${s} »`).join('\n')}`,
        `\nDIRECTION ARTISTIQUE : ${idea.visualDirection}`,
      ].join('\n');
      const id = jobs.enqueue({
        analysisType: 'concept',
        label: conceptOption.label,
        inputSummary: `Visaly Créa → ${idea.territory}`,
        payload: {
          textContent,
          context: `Idée générée par Visaly Créa. TERRITOIRE CRÉATIF CHOISI : « ${idea.territory} ». IMPORTANT : toute suggestion de version conforme DOIT rester dans ce territoire créatif. Ne pas dériver vers un autre axe (savoir-faire, pédagogie santé, etc.) sauf si le territoire choisi est fondamentalement incompatible avec la réglementation de la publicité des produits de santé.`,
        },
      });
      setExternalResult(null);
      jobs.setActiveId(id);
      setStep('result');
    },
    [jobs],
  );

  const handleBack = useCallback(() => {
    if (step === 'upload') {
      setSelectedOption(null);
      setStep('select');
    } else if (step === 'result') {
      setStep('upload');
    }
  }, [step]);

  const handleReset = useCallback(() => {
    setSelectedOption(null);
    setError(null);
    setExternalResult(null);
    jobs.setActiveId(null);
    setStep('select');
    // Rafraîchit les crédits par type : un crédit consommé se reflète aussitôt.
    void refreshProfile();
  }, [refreshProfile, jobs]);

  const handleViewHistory = useCallback(
    (item: SidebarItem) => {
      // Backfill originalInput pour les anciens items sans cette donnée.
      if (item.result && !item.result.originalInput && item.title
        && !/\.(jpe?g|png|gif|webp|bmp|svg|heic)$/i.test(item.title)) {
        item.result.originalInput = item.title;
      }
      setExternalResult(null);
      jobs.setActiveId(item.id);
      jobs.markReadByItem(item.id);
      setSelectedOption(ANALYSIS_OPTIONS.find((o) => o.key === item.analysisType) ?? null);
      setStep('result');
      setIsMobileMenuOpen(false);
    },
    [jobs],
  );

  const handleDeleteHistory = useCallback(
    (id: string) => {
      const wasActive = jobs.activeId === id;
      jobs.deleteItem(id);
      if (wasActive) handleReset();
    },
    [jobs, handleReset],
  );

  // Ancre une réécriture conforme générée à son analyse (live + cache).
  const handleSuggestionSaved = useCallback(
    (fix: SuggestedFix) => {
      if (externalResult) {
        setExternalResult((prev) => (prev ? { ...prev, result: { ...prev.result, suggestedFix: fix } } : prev));
        return;
      }
      if (jobs.activeId && activeItem?.result) {
        jobs.updateResult(jobs.activeId, { ...activeItem.result, suggestedFix: fix });
      }
    },
    [externalResult, jobs, activeItem],
  );

  // Ouvre une analyse depuis une notification.
  const handleOpenNotification = useCallback(
    (notif: AppNotification) => {
      const item = jobs.items.find((i) => i.id === notif.itemId);
      jobs.markRead(notif.id);
      if (!item) return;
      setExternalResult(null);
      jobs.setActiveId(item.id);
      setSelectedOption(ANALYSIS_OPTIONS.find((o) => o.key === item.analysisType) ?? null);
      setStep('result');
    },
    [jobs],
  );

  // Pill « analyse en cours » → revient sur l'analyse active la plus récente.
  const handleResumeLoader = useCallback(() => {
    const running = jobs.items.find((i) => i.status === 'running') ?? jobs.items.find((i) => i.status === 'queued');
    if (!running) return;
    setExternalResult(null);
    jobs.setActiveId(running.id);
    setStep('result');
  }, [jobs]);

  function renderAnalysisView() {
    if (externalResult) {
      const r = externalResult.result;
      if (r.creativeResult) {
        return <CreativeResults creativeResult={r.creativeResult} sources={r.sources} onNewAnalysis={handleReset} onDeepAnalyze={handleDeepAnalyze} />;
      }
      return (
        <ResultsDisplay
          key="external"
          result={r}
          onNewAnalysis={handleReset}
          onSuggestionSaved={handleSuggestionSaved}
          analysisLabel={externalResult.option?.label}
          analysisType={externalResult.option?.key}
        />
      );
    }
    if (!activeItem) return null;
    if (activeItem.status === 'queued' || activeItem.status === 'running') {
      return (
        <Loader
          analysisType={activeItem.analysisLabel}
          analysisKey={activeItem.analysisType}
          progress={activeItem.status === 'queued' ? null : activeItem.progress ?? null}
          progressLabel={activeItem.status === 'queued' ? "En file d'attente…" : activeItem.progressLabel ?? null}
        />
      );
    }
    if (activeItem.status === 'error') {
      return (
        <div className="animate-fade-in max-w-md mx-auto text-center py-16">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-200 text-ink mb-4">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
          </div>
          <h3 className="font-display text-lg text-slate-900 mb-1">L'analyse a échoué</h3>
          <p className="text-sm text-slate-500 mb-2">{activeItem.errorMessage ?? 'Une erreur est survenue.'}</p>
          <p className="text-xs text-slate-400 mb-6">Aucun crédit n'a été débité.</p>
          <div className="flex items-center justify-center gap-2">
            <button onClick={handleReset} className="btn-soft font-display px-4 py-2 text-sm">Retour</button>
            <button onClick={() => jobs.retryItem(activeItem.id)} className="btn-ink font-display px-4 py-2 text-sm">Réessayer</button>
          </div>
        </div>
      );
    }
    if (activeItem.result?.creativeResult) {
      return <CreativeResults creativeResult={activeItem.result.creativeResult} sources={activeItem.result.sources} onNewAnalysis={handleReset} onDeepAnalyze={handleDeepAnalyze} />;
    }
    if (activeItem.result) {
      return (
        <ResultsDisplay
          key={activeItem.id}
          result={activeItem.result}
          onNewAnalysis={handleReset}
          onSuggestionSaved={handleSuggestionSaved}
          analysisLabel={activeOption?.label}
          analysisType={activeOption?.key}
        />
      );
    }
    return null;
  }

  /* Repère coloré de navigation : dès qu'on entre dans une analyse, la couleur
     de la tuile d'où l'on vient habille l'écran jusqu'au retour. Le FOND, lui,
     reste le crème applicatif — la teinte passe par les éléments (en-tête,
     champs, puces) via les variables `--tint-*` posées ici, et par la classe
     `tinted-fields` qui colore toutes les saisies de l'écran. */
  const tintedStep = step === 'upload' || step === 'result' || step === 'loading';
  const tintedType = step === 'upload'
    ? selectedOption?.key
    : externalResult?.option?.key ?? activeOption?.key ?? selectedOption?.key;
  const inAnalysis = tintedStep && !!tintedType;

  // Onglet actif — pilote la barre rose de la sidebar et la barre d'onglets mobile.
  const activeNav: SidebarNav =
    step === 'select' ? 'new'
    : step === 'historique' ? 'history'
    : step === 'pricing' ? 'pricing'
    : step === 'account' ? 'account'
    : null;

  return (
    <AuthGate>
    {/* ── Coquille « Cream & Ink » ───────────────────────────────────
        Fond crème pleine page, puis un cadre arrondi qui contient toute
        l'application — la fenêtre flottante de la charte. */}
    <div className="app-shell relative h-screen overflow-hidden p-0 sm:p-3 md:p-4">
      <div className="app-frame relative flex h-full w-full overflow-hidden rounded-none sm:rounded-[26px] md:rounded-[32px]">

      <TermsGate />
      <DisclaimerModal />
      <AnalysisToast toasts={jobs.toasts} onOpen={handleOpenNotification} onDismiss={jobs.dismissToast} />
      <LegalNotice
        visible={legalVisible}
        onClose={() => setLegalVisible(false)}
        onNavigate={(target) => {
          setLegalVisible(false);
          setAccountInitialTab(target);
          setIsAccountOpen(true);
        }}
        onOpenDoc={(doc) => {
          if (doc === 'privacy') setPrivacyVisible(true);
          else if (doc === 'terms') setTermsVisible(true);
          else if (doc === 'subprocessors') setSubprocessorsVisible(true);
        }}
      />
      <PrivacyPolicy
        visible={privacyVisible}
        onClose={() => setPrivacyVisible(false)}
        onOpenSubprocessors={() => setSubprocessorsVisible(true)}
      />
      <TermsOfService
        visible={termsVisible}
        onClose={() => setTermsVisible(false)}
      />
      <Subprocessors
        visible={subprocessorsVisible}
        onClose={() => setSubprocessorsVisible(false)}
      />

      {/* Sidebar + popover compte — masqués pendant l'onboarding pour focaliser
          le prospect sur l'action centrale (abonnement / bundle / RDV). */}
      {!isOnboarding && (
        <>
          {/* Gouttière crème autour du panneau noir. `h-full` sur le conteneur
              ET sur l'aside : le panneau descend jusqu'en bas du cadre même
              quand l'historique est vide. */}
          <div className="relative z-40 h-full flex-shrink-0 md:py-3.5 md:pl-3.5">
            <Sidebar
              history={jobs.items}
              onViewHistory={handleViewHistory}
              onRenameHistory={jobs.renameItem}
              onDeleteHistory={handleDeleteHistory}
              activeItemId={jobs.activeId}
              isMobileOpen={isMobileMenuOpen}
              setIsMobileOpen={setIsMobileMenuOpen}
              isCollapsed={isSidebarCollapsed}
              setIsCollapsed={setIsSidebarCollapsed}
              onAccountClick={() => setIsAccountOpen(prev => !prev)}
              onHomeClick={handleReset}
              onHistoriqueClick={() => { setStep('historique'); setIsMobileMenuOpen(false); }}
              onPricingClick={() => { setPricingScrollTo(undefined); setStep('pricing'); setIsMobileMenuOpen(false); }}
              activeNav={activeNav}
            />
          </div>

          {/* Account popover — rendered at root level to avoid stacking context issues */}
          <AccountPopover
            isOpen={isAccountOpen}
            onClose={() => { setIsAccountOpen(false); setAccountInitialTab('menu'); }}
            isSidebarCollapsed={isSidebarCollapsed}
            onLegalClick={() => setLegalVisible(true)}
            onSettingsClick={() => setStep('account')}
            onUpgradeClick={() => setStep('pricing')}
            onHistoriqueClick={() => setStep('historique')}
            onAdminClick={() => setStep('admin')}
            initialTab={accountInitialTab}
          />
        </>
      )}

      {/* Right column: header + content + footer */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 z-10">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(prev => !prev)}
          runningCount={jobs.runningCount}
          queuedCount={jobs.queuedCount}
          onResumeLoader={handleResumeLoader}
          notifications={jobs.notifications}
          unreadCount={jobs.unreadCount}
          onOpenNotification={handleOpenNotification}
          onMarkAllNotificationsRead={jobs.markAllRead}
          onDismissNotification={jobs.dismiss}
        />

        <main
          className={`scrollbar-visaly flex-1 overflow-y-auto overflow-x-hidden ${inAnalysis ? 'tinted-fields' : ''}`}
          style={{ backgroundColor: APP_SURFACE, ...tintVars(tintedType) }}
        >
          <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-8">
            {step === 'select' && isOnboarding && (
              <OnboardingScreen
                onViewFullPricing={() => { setPricingScrollTo('bundles'); setStep('pricing'); }}
              />
            )}

            {step === 'select' && !isOnboarding && (
              <AnalysisTypeSelector
                options={ANALYSIS_OPTIONS}
                onSelect={handleSelectType}
                onUpgrade={() => { setPricingScrollTo(undefined); setStep('pricing'); }}
                onUnitPurchase={() => { setPricingScrollTo('bundles'); setStep('pricing'); }}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key === 'social_post' && (
              <SocialPostHub
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
                creditsAvailable={features.unlimited && features.analysesSocial ? null : (organization?.creditsPerType?.social_post ?? 0)}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key === 'creative_brief' && (
              <CreativeBriefForm
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key !== 'social_post' && selectedOption.key !== 'creative_brief' && (
              <ContentUploader
                option={selectedOption}
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
              />
            )}

            {(step === 'loading' || step === 'result') && renderAnalysisView()}

            {step === 'pricing' && (
              <PricingPage onClose={handleReset} scrollTo={pricingScrollTo} />
            )}

            {step === 'historique' && (
              <HistoriquePage
                onClose={handleReset}
                onViewAnalysis={(result, analysisType) => {
                  const option = ANALYSIS_OPTIONS.find((o) => o.key === (analysisType as AnalysisType)) ?? null;
                  jobs.setActiveId(null);
                  setExternalResult({ result, option });
                  setStep('result');
                }}
              />
            )}

            {step === 'account' && (
              <AccountSettingsPage
                onClose={handleReset}
                onUpgrade={() => setStep('pricing')}
              />
            )}

            {step === 'admin' && (
              <Suspense fallback={<div className="text-center py-12 text-slate-500">Chargement...</div>}>
                <AdminDashboard onClose={handleReset} />
              </Suspense>
            )}
          </div>
        </main>

        <Footer
          onLegalClick={() => setLegalVisible(true)}
          onPrivacyClick={() => setPrivacyVisible(true)}
          onTermsClick={() => setTermsVisible(true)}
          onSubprocessorsClick={() => setSubprocessorsVisible(true)}
        />

        {/* Barre d'onglets noire — équivalent mobile de la sidebar */}
        {!isOnboarding && (
          <MobileTabBar
            active={
              step === 'select' ? 'home'
              : step === 'historique' ? 'history'
              : step === 'pricing' ? 'pricing'
              : step === 'account' ? 'account'
              : null
            }
            onHome={handleReset}
            onHistory={() => setStep('historique')}
            onPricing={() => { setPricingScrollTo(undefined); setStep('pricing'); }}
            onAccount={() => setStep('account')}
            onNew={handleReset}
            badge={jobs.unreadCount}
          />
        )}
      </div>
      </div>
    </div>
    </AuthGate>
  );
}
